/*
 * Apothecary Porch zone — the elves' apothecary as seen from the critter scale.
 *
 * Everything here is OVERSIZED relative to the critter (scale-as-identity per
 * AESTHETIC.md §5): the porch planks, the potion shelves, the dried herbs
 * hanging from the rafters all loom large. The elves are off-screen per LORE.md —
 * we never render them, but their work is everywhere.
 *
 * The container returned here replaces the "background" stack of the square
 * scene when the zone is active. Critters / lanterns / overlays continue to
 * render on top of whatever zone is showing.
 */

import { Container, FillGradient, Graphics } from 'pixi.js';
import { PALETTE, WORLD } from '@/render/palette';

export function createApothecaryBackdrop(): Container {
  const root = new Container();

  // Sky still visible above the porch eaves — same twilight gradient as the
  // square so a player who walks through doesn't perceive a color shift.
  const sky = new Graphics();
  const grad = new FillGradient(0, 0, 0, WORLD.height);
  grad.addColorStop(0, PALETTE.twilightDeep);
  grad.addColorStop(0.25, PALETTE.twilight);
  grad.addColorStop(0.45, PALETTE.twilightMid);
  grad.addColorStop(0.6, 0x4a3520);
  grad.addColorStop(1, 0x2d1f0e);
  sky.rect(0, 0, WORLD.width, WORLD.height).fill(grad);
  root.addChild(sky);

  // Porch roof beam — a thick dark wooden bar across the top quarter.
  const roof = new Graphics();
  roof.rect(0, 110, WORLD.width, 16).fill(0x3a2410);
  // Beam underside shadow.
  roof.rect(0, 124, WORLD.width, 4).fill({ color: 0x000000, alpha: 0.4 });
  // Rafter supports.
  for (const rx of [40, 180, 320, 460, 580]) {
    roof.rect(rx - 4, 110, 8, 28).fill(0x3a2410);
  }
  root.addChild(roof);

  // Dried herbs hanging from the rafters.
  for (let i = 0; i < 5; i++) {
    const hx = 90 + i * 100;
    const len = 14 + Math.sin(i * 1.7) * 4;
    const herbs = new Graphics();
    // String.
    herbs.moveTo(hx, 138).lineTo(hx, 138 + len).stroke({ color: 0xc7b48a, width: 0.8 });
    // Bundle.
    herbs.ellipse(hx, 138 + len + 6, 4, 7).fill(0x5a8a5a);
    herbs.ellipse(hx - 2, 138 + len + 4, 1.6, 4).fill(0x3e6e4f);
    herbs.ellipse(hx + 2, 138 + len + 8, 1.6, 4).fill(0x3e6e4f);
    // A pop of color on alternate bundles.
    if (i % 2 === 0) {
      herbs.circle(hx, 138 + len + 4, 1.2).fill(0xc9514c);
    }
    root.addChild(herbs);
  }

  // Shelf at left — potion bottles on it.
  const leftShelf = new Graphics();
  leftShelf.rect(20, 280, 140, 6).fill(0x5c3d28);
  leftShelf.rect(20, 286, 140, 2).fill({ color: 0x000000, alpha: 0.4 });
  // Bottles.
  const bottles: Array<readonly [number, number, number, number]> = [
    [40, 260, 12, 0xc9514c],
    [62, 262, 10, 0x4a7090],
    [82, 258, 13, 0xffc56b],
    [102, 264, 9, 0x6ba07a],
    [122, 260, 11, 0xfff4dc],
    [142, 263, 10, 0x7a5a8a],
  ];
  for (const [bx, by, bh, color] of bottles) {
    // Body.
    leftShelf.rect(bx - 3, by, 6, bh).fill(color);
    // Cork.
    leftShelf.rect(bx - 2, by - 2, 4, 2).fill(0x6b4423);
    // Soft glow.
    leftShelf.circle(bx, by + bh / 2, 5).fill({ color, alpha: 0.25 });
  }
  root.addChild(leftShelf);

  // Shelf at right — more bottles.
  const rightShelf = new Graphics();
  rightShelf.rect(440, 280, 140, 6).fill(0x5c3d28);
  rightShelf.rect(440, 286, 140, 2).fill({ color: 0x000000, alpha: 0.4 });
  const bottles2: Array<readonly [number, number, number, number]> = [
    [460, 262, 11, 0x4a7090],
    [480, 258, 13, 0xffc56b],
    [500, 264, 9, 0xc9514c],
    [520, 260, 12, 0x6ba07a],
    [540, 263, 10, 0xfff4dc],
    [560, 261, 12, 0x4a3b6b],
  ];
  for (const [bx, by, bh, color] of bottles2) {
    rightShelf.rect(bx - 3, by, 6, bh).fill(color);
    rightShelf.rect(bx - 2, by - 2, 4, 2).fill(0x6b4423);
    rightShelf.circle(bx, by + bh / 2, 5).fill({ color, alpha: 0.25 });
  }
  root.addChild(rightShelf);

  // Mortar and pestle on a small table in the middle-back.
  const table = new Graphics();
  // Table top.
  table.roundRect(255, 360, 90, 8, 3).fill(0x6b4423);
  // Legs.
  table.rect(258, 368, 4, 18).fill(0x5c3d28);
  table.rect(338, 368, 4, 18).fill(0x5c3d28);
  // Mortar — fat cream bowl.
  table.ellipse(300, 358, 14, 8).fill(0xc7b48a);
  table.ellipse(300, 356, 11, 4).fill({ color: 0x000000, alpha: 0.45 });
  // Pestle leaning.
  table.rect(310, 348, 2, 14).fill(0x5c3d28);
  root.addChild(table);

  // Wooden porch planks for the floor — long horizontal strips with grain.
  const floor = new Graphics();
  for (let i = 0; i < 6; i++) {
    const py = 460 + i * 50;
    floor.rect(0, py, WORLD.width, 50).fill(i % 2 === 0 ? 0x4d3a25 : 0x40301f);
    floor.rect(0, py, WORLD.width, 1).fill({ color: 0x000000, alpha: 0.45 });
    // A couple of plank-grain knots per row.
    for (let k = 0; k < 3; k++) {
      const kx = 80 + k * 200 + (i % 2 === 1 ? 60 : 0);
      floor.ellipse(kx, py + 25, 5, 2).fill({ color: 0x000000, alpha: 0.25 });
    }
  }
  root.addChild(floor);

  // Soft pool of candle-light spilling onto the floor in the middle.
  const glow = new Graphics();
  glow.circle(WORLD.width / 2, 500, 220).fill({ color: 0xffc56b, alpha: 0.06 });
  glow.circle(WORLD.width / 2, 500, 140).fill({ color: 0xffc56b, alpha: 0.08 });
  root.addChild(glow);

  // A single candle on the table for the warmth source.
  const candle = new Graphics();
  candle.rect(289, 348, 4, 10).fill(0xfff4dc);
  candle.ellipse(291, 346, 2, 3).fill(0xffe9a8);
  candle.circle(291, 346, 5).fill({ color: 0xffe9a8, alpha: 0.5 });
  root.addChild(candle);

  return root;
}
